import { camelCase, isEmpty, upperFirst, words } from 'lodash';
import { defaultProgressBarSections } from '../../../shopConfig/config';
import { ListingFlowSection, ProgressBarSection } from '../../../types/shopConfig/shopConfigV2';
import { trackCompleteListingSection } from '../../../util/heap';
import { ItemDetailsFormState, ListingFlowFormField } from '../ListingFlowTypes';

export const getSectionsToFormValuesAndErrors = (formState: ItemDetailsFormState) => {
  const {
    // TODO (abbykrish): This was causing a super gnarly circular dependency that we could not fix.
    errors: {
      findItem: findItemErrors,
      price: priceErrors,
      images: imageErrors,
      condition: conditionErrors,
      acknowledgement: acknowledgementErrors,
      featuredImageID,
      authentication,
      itemType,
      ...itemDetailVariantOptionErrors
    },
    values: {
      findItem: findItemValues,
      price: priceValues,
      images: imageValues,
      condition: conditionValues,
      acknowledgement: acknowledgementValues,
      ...itemDetailVariantOptionValues
    },
  } = formState;

  return {
    [ProgressBarSection.FindItem]: {
      values: findItemValues,
      errors: findItemErrors,
    },
    [ProgressBarSection.ItemDetails]: {
      values: itemDetailVariantOptionValues,
      errors: itemDetailVariantOptionErrors,
    },
    [ProgressBarSection.Condition]: {
      values: conditionValues,
      errors: conditionErrors,
    },
    [ProgressBarSection.Photos]: {
      values: imageValues,
      errors: imageErrors || featuredImageID,
    },
    [ProgressBarSection.Price]: {
      values: priceValues,
      errors: priceErrors,
    },
    [ProgressBarSection.Acknowledgement]: {
      values: acknowledgementValues,
      errors: acknowledgementErrors,
    },
  };
};

const getIsSectionRelevant = (
  section: ProgressBarSection,
  itemDetailsFormSectionsToShow: ListingFlowSection[]
) => {
  // Need a special case for FindItem because it does not have a matching section
  // on the ItemDetailsForm.
  if (section === ProgressBarSection.FindItem) {
    return true;
  }

  const progressBarSectionToListingFlowSection: {
    [key in ProgressBarSection]?: ListingFlowSection;
  } = {
    [ProgressBarSection.ItemDetails]: ListingFlowSection.VariantOptions,
    [ProgressBarSection.Condition]: ListingFlowSection.Condition,
    [ProgressBarSection.Photos]: ListingFlowSection.Photos,
    [ProgressBarSection.Price]: ListingFlowSection.Price,
    [ProgressBarSection.Acknowledgement]: ListingFlowSection.AcknowledgementCheckbox,
  };

  const mappedListingFlowSection = progressBarSectionToListingFlowSection[section];

  return (
    !!mappedListingFlowSection && itemDetailsFormSectionsToShow.includes(mappedListingFlowSection)
  );
};

export const getRelevantSections = (itemDetailsFormSectionsToShow: ListingFlowSection[]) =>
  defaultProgressBarSections.filter((section) =>
    getIsSectionRelevant(section, itemDetailsFormSectionsToShow)
  );

export const getCompletedSections = (
  sections: ProgressBarSection[],
  sectionsToFormValuesAndErrors: ReturnType<typeof getSectionsToFormValuesAndErrors>
) =>
  sections.filter((section) => {
    const sectionValuesAndErrors = sectionsToFormValuesAndErrors[section];
    const hasNoErrors = isEmpty(sectionValuesAndErrors.errors);
    return hasNoErrors;
  });

export const getProgressBarCompletionStates = (
  sections: ProgressBarSection[],
  completedSections: ProgressBarSection[]
) =>
  sections.map((section) => {
    const sectionNameAsWords = words(section);
    const sectionDisplayName = sectionNameAsWords
      .map((word) => upperFirst(word.toLowerCase()))
      .join(' ');
    const isComplete = completedSections.includes(section);
    return {
      isComplete,
      displayName: sectionDisplayName,
    };
  });

export const getNewlyCompletedSectionNameToCompletionTime = (
  completedSections: ProgressBarSection[],
  listingFlowSectionToTimestamp: { [sectionName: string]: number }
) => {
  const completionTime = Date.now().valueOf();
  return completedSections.reduce((acc, section) => {
    const timeCompletedName = camelCase(section);
    if (!listingFlowSectionToTimestamp?.[timeCompletedName]) {
      return { ...acc, [timeCompletedName]: completionTime };
    }
    return acc;
  }, {});
};

const getAlreadyCompletedSections = (listingFlowSectionToTimestamp: {
  [sectionName: string]: number;
}) => Object.keys(listingFlowSectionToTimestamp).sort().join(',');

const getMostRecentlyCompletedSection = (listingFlowSectionToTimestamp: {
  [sectionName: string]: number;
}) => {
  const listingFlowTimestampEntries = Object.entries(listingFlowSectionToTimestamp);
  if (!listingFlowTimestampEntries.length) {
    return {};
  }
  const [[mostRecentlyCompletedSection, mostRecentlyCompletedSectionFinishTime]] =
    listingFlowTimestampEntries.sort(([, tsA], [, tsB]) => tsB - tsA);
  return { mostRecentlyCompletedSection, mostRecentlyCompletedSectionFinishTime };
};

export const trackCompletedListingSections = (
  listingStartTime: number,
  allListingFlowSectionToTimestamp: { [sectionName: string]: number },
  newlyCompletedListingFlowSectionToTimestamp: { [sectionName: string]: number },
  passedInEventProperties: {
    itemType?: string;
    listingFlowId: string;
    findItemMethod?: string;
    didUseQRCode?: string;
  }
) => {
  Object.entries(newlyCompletedListingFlowSectionToTimestamp).forEach(
    ([sectionName, timestamp]) => {
      if (!timestamp || !listingStartTime) {
        return;
      }

      const alreadyCompletedSections = getAlreadyCompletedSections(
        allListingFlowSectionToTimestamp
      );
      const { mostRecentlyCompletedSection, mostRecentlyCompletedSectionFinishTime } =
        getMostRecentlyCompletedSection(allListingFlowSectionToTimestamp);
      const sectionStartTime = mostRecentlyCompletedSectionFinishTime || listingStartTime;
      const totalTimeSpent = timestamp - sectionStartTime;
      const eventProperties = {
        alreadyCompletedSections,
        mostRecentlyCompletedSection,
        sectionName,
        totalTimeSpent,
        ...passedInEventProperties,
      };

      if (!Number.isNaN(totalTimeSpent)) {
        trackCompleteListingSection(eventProperties);
      }
    }
  );
};
